// Contiguous phase-date chaining for multi-phase rolloff projects.
//
// A project's phases form one continuous timeline. Adjacent phases share a
// single "swap hinge" date: phase[i].endDate === phase[i+1].startDate. So a
// sequence of N phases is really N+1 boundary points B0..BN:
//
//   B0 ── phase 1 ── B1 ── phase 2 ── B2 ── … ── B(N-1) ── phase N ── BN
//
//   - B0  = phase 1's delivery (the only freely-movable start)
//   - BN  = phase N's pickup   (the only freely-movable end)
//   - Bk  = phase k's pickup === phase k+1's delivery (an interior hinge)
//
// Editing any boundary necessarily moves both phases that touch it. The
// cascade *mode* decides whether the change stops at the immediate neighbor or
// ripples onward:
//
//   - 'absorb' : move only the edited boundary. The neighbor on the far side
//                keeps its other edge, so its duration changes; nothing
//                downstream moves.
//   - 'ripple' : move the edited boundary AND shift every later boundary by the
//                same delta, preserving the duration of each downstream phase
//                (they slide forward/back together).
//
// Ripple never crosses a promoted phase — a promoted phase is a live Booqable
// reservation and is immutable in this flow (draft-only), so the shift clamps
// before it.
//
// All dates here are YYYY-MM-DD strings (the calendar's wire format). null means
// "not set yet" — a partially-built sequence is valid, and ripple with a missing
// reference point degrades to absorb.

export interface EditablePhase {
  /** Underlying order id. */
  id: string;
  /** 1-based position in the swap sequence. */
  phaseNumber: number;
  /** Delivery date (YYYY-MM-DD) or null. */
  startDate: string | null;
  /** Pickup date (YYYY-MM-DD) or null. */
  endDate: string | null;
  /** Promoted to Booqable — its window is immutable in this flow. */
  promoted: boolean;
}

export type CascadeMode = 'absorb' | 'ripple';

export interface PhaseEdit {
  /** Which phase's picker fired. */
  phaseId: string;
  /** 'start' moves the phase's delivery boundary; 'end' its pickup boundary. */
  edge: 'start' | 'end';
  /** New value (YYYY-MM-DD) or null to clear. */
  date: string | null;
  mode: CascadeMode;
}

// --- YYYY-MM-DD date math (no Date-object timezone drift) ---------------------

const ymdToUtc = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const utcToYmd = (ms: number): string => {
  const dt = new Date(ms);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DAY_MS = 86_400_000;

/** Whole-day delta (b - a) between two YYYY-MM-DD strings. */
export const diffDays = (a: string, b: string): number =>
  Math.round((ymdToUtc(b) - ymdToUtc(a)) / DAY_MS);

/** Shift a YYYY-MM-DD string by a whole number of days. */
export const addDays = (ymd: string, days: number): string =>
  utcToYmd(ymdToUtc(ymd) + days * DAY_MS);

// --- Cascade ------------------------------------------------------------------

/**
 * Apply one date edit to the phase sequence, returning a new array (input is
 * not mutated). Phases are reordered by phaseNumber internally so callers can
 * pass them in any order.
 */
export function applyPhaseEdit(
  phases: EditablePhase[],
  edit: PhaseEdit,
): EditablePhase[] {
  const ordered = [...phases].sort((a, b) => a.phaseNumber - b.phaseNumber);
  const idx = ordered.findIndex((p) => p.id === edit.phaseId);
  if (idx === -1) return phases;

  const n = ordered.length;
  // Boundary array B0..BN (length n+1). B0 = first delivery; Bk = phase k-1's
  // pickup (== phase k's delivery) for k = 1..n.
  const boundaries: (string | null)[] = new Array(n + 1).fill(null);
  boundaries[0] = ordered[0].startDate;
  for (let k = 1; k <= n; k++) boundaries[k] = ordered[k - 1].endDate;

  // 'start' edits the phase's left boundary (index idx); 'end' the right (idx+1).
  const bIdx = edit.edge === 'start' ? idx : idx + 1;
  const oldVal = boundaries[bIdx];
  const newVal = edit.date;

  boundaries[bIdx] = newVal;

  if (edit.mode === 'ripple' && oldVal && newVal) {
    const delta = diffDays(oldVal, newVal);
    if (delta !== 0) {
      for (let k = bIdx + 1; k <= n; k++) {
        // Boundary k is shared by ordered[k-1] (its pickup) and ordered[k] (its
        // delivery). Moving it would alter both, so stop BEFORE the move if
        // either side is a promoted (immutable) phase — the last draft phase
        // before the wall then absorbs the change.
        if (ordered[k - 1].promoted) break;
        if (ordered[k]?.promoted) break;
        if (boundaries[k]) boundaries[k] = addDays(boundaries[k] as string, delta);
      }
    }
  }

  // Rebuild phases from the (possibly shifted) boundaries — hinges stay
  // consistent because phase k's end and phase k+1's start read the same B.
  return ordered.map((p, k) => ({
    ...p,
    startDate: boundaries[k],
    endDate: boundaries[k + 1],
  }));
}

/**
 * Reconcile a locally-edited schedule with a fresh server schedule after the
 * phase SET changed (e.g. a swap was added), WITHOUT discarding unsaved edits.
 *
 * The server is the source of truth for which phases exist and their order; the
 * local copy is the source of truth for dates the user has edited but not yet
 * saved. For each server phase we keep the local dates when we still have a
 * match by id; a brand-new phase (no local match) is chained onto the previous
 * phase's (possibly edited) end so the contiguous chain stays intact, keeping
 * the server-assigned duration. Removed phases simply fall away (not in server).
 */
export function mergeScheduleWithServer(
  local: EditablePhase[],
  server: EditablePhase[],
): EditablePhase[] {
  const localById = new Map(local.map((p) => [p.id, p]));
  const ordered = [...server].sort((a, b) => a.phaseNumber - b.phaseNumber);
  const result: EditablePhase[] = [];
  for (const s of ordered) {
    const existing = localById.get(s.id);
    if (existing) {
      // Keep the user's unsaved dates; trust the server for order + promotion.
      result.push({ ...existing, phaseNumber: s.phaseNumber, promoted: s.promoted });
      continue;
    }
    // New phase. Chain its start onto the previous phase's (edited) end so the
    // contiguous chain stays intact off the UNSAVED hinge — a freshly-added phase
    // comes back with the previous phase's *saved* start and no end yet, so we
    // can't gate on it having a full window. Preserve the server-assigned
    // duration when one exists; otherwise leave the end unset for the user to
    // pick. Fall back to the server window only when there's no usable hinge.
    const prev = result[result.length - 1];
    if (prev?.endDate) {
      const duration =
        s.startDate && s.endDate ? Math.max(1, diffDays(s.startDate, s.endDate)) : null;
      result.push({
        ...s,
        startDate: prev.endDate,
        endDate: duration != null ? addDays(prev.endDate, duration) : s.endDate,
      });
    } else {
      result.push(s);
    }
  }
  return result;
}

/** Has any phase's window changed between two sequences (keyed by id)? */
export function scheduleChanged(
  a: EditablePhase[],
  b: EditablePhase[],
): boolean {
  const bById = new Map(b.map((p) => [p.id, p]));
  if (a.length !== b.length) return true;
  return a.some((p) => {
    const other = bById.get(p.id);
    return !other || other.startDate !== p.startDate || other.endDate !== p.endDate;
  });
}
